/**
 * @file Ads Analytics 機能全体で使用される定数、型定義、ラベル、オプションを定義します。
 * プラットフォーム種別、キャンペーンパフォーマンスグループ化基準、フィルター状態などが含まれます。
 */

import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { AdsMetricKey } from "./_metrics/ads-metrics";

/** Firestore `/features` で有効化する Ads Analytics の feature flag 名。 */
export const ADS_ANALYTICS_FEATURE = "ads_analytics" as const;

// #region AdsPlatform
export const AdsPlatforms = [
  "GOOGLE_ADS",
  "META_ADS",
  "YAHOO_SPONSORED_SEARCH",
  "YAHOO_DISPLAY",
] as const;
export type AdsPlatform = (typeof AdsPlatforms)[number];

export const AdsPlatformLabels: Record<AdsPlatform, ReturnType<typeof msg>> = {
  GOOGLE_ADS: msg`Google Ads`,
  META_ADS: msg`Meta Ads`,
  YAHOO_SPONSORED_SEARCH: msg`Yahoo! Sponsored Search`,
  YAHOO_DISPLAY: msg`Yahoo! Display Ads`,
};
// #endregion

// #region AdsDevice
/** 広告プラットフォームから取り込む既知のデバイス値。未定義の値は元の文字列を表示する。 */
const AdsDeviceLabels: Record<string, ReturnType<typeof msg>> = {
  DESKTOP: msg`Desktop`,
  MOBILE: msg`Mobile`,
  TABLET: msg`Tablet`,
  CONNECTED_TV: msg`Connected TV`,
  OTHER: msg`Other`,
  UNKNOWN: msg`Unknown`,
  UNSPECIFIED: msg`Unspecified`,
};

/** 既知のデバイス値だけ翻訳し、将来の広告プラットフォーム固有値はそのまま表示する。 */
function getAdsDeviceLabel(device: string): ReturnType<typeof msg> | undefined {
  return AdsDeviceLabels[device.toUpperCase()];
}

/** 既知のデバイス値を翻訳し、未知値は外部プラットフォームから受け取った原文を表示する。 */
export function formatAdsDeviceLabel(i18n: I18n, device: string): string {
  const label = getAdsDeviceLabel(device);
  return label ? i18n._(label) : device;
}
// #endregion

// #region CampaignPerformanceEntityGroupBy
const CampaignEntityGroupBys = [
  "PLATFORM",
  "CAMPAIGN",
  "AD_GROUP",
  "DEVICE",
  "NETWORK",
  "KEYWORD",
  "SEARCH_QUERY",
] as const;
export type CampaignEntityGroupBy = (typeof CampaignEntityGroupBys)[number];

export const CampaignEntityGroupByLabels: Record<
  CampaignEntityGroupBy,
  ReturnType<typeof msg>
> = {
  PLATFORM: msg`Platform`,
  CAMPAIGN: msg`Campaign`,
  AD_GROUP: msg`Ad Group`,
  DEVICE: msg`Device`,
  NETWORK: msg`Network`,
  KEYWORD: msg`Keyword`,
  SEARCH_QUERY: msg`Search Query`,
};

export const CampaignEntityGroupByOptions = CampaignEntityGroupBys.map(
  (groupBy) => ({
    value: groupBy,
    label: CampaignEntityGroupByLabels[groupBy],
  }),
);
// #endregion

// #region AdsFilterState
/** Ads Analytics のフィルター状態型。 */
export type AdsFilterState = {
  platforms: AdsPlatform[];
  campaigns: string[];
  adGroups: string[];
  devices: string[];
  network1s: string[];
  network2s: string[];
};

export const DEFAULT_ADS_FILTER: AdsFilterState = {
  platforms: [],
  campaigns: [],
  adGroups: [],
  devices: [],
  network1s: [],
  network2s: [],
};

/** フィルターディメンションの表示ラベル（SA/UA の FilterFieldLabels と同じ命名規約）。 */
export const AdsFilterFieldLabels: Record<
  keyof AdsFilterState,
  ReturnType<typeof msg>
> = {
  platforms: msg`Platform`,
  campaigns: msg`Campaign`,
  adGroups: msg`Ad Group`,
  devices: msg`Device`,
  network1s: msg`Network 1`,
  network2s: msg`Network 2`,
};
// #endregion

// #region OrderDirection
const OrderDirections = ["ASC", "DESC"] as const;
export type OrderDirection = (typeof OrderDirections)[number];
// #endregion

// #region CampaignPerformanceDetailOrderField
const CampaignDetailOrderFields = [
  "IMPRESSIONS",
  "CLICKS",
  "COST",
  "CONVERSIONS",
  "CONVERSION_VALUE",
  "CTR",
  "CVR",
  "AVERAGE_CPC",
  "COST_PER_CONVERSION",
  "CONVERSION_VALUE_PER_COST",
] as const;
export type CampaignDetailOrderField =
  (typeof CampaignDetailOrderFields)[number];

/** APIで並び替え可能な指標。表の取得とビュー保存で同じ対応を使用する。 */
export const ADS_ORDER_FIELD_BY_METRIC: Partial<
  Record<AdsMetricKey, CampaignDetailOrderField>
> = {
  impressions: "IMPRESSIONS",
  clicks: "CLICKS",
  cost: "COST",
  conversions: "CONVERSIONS",
  conversionValue: "CONVERSION_VALUE",
  ctr: "CTR",
  cvr: "CVR",
  averageCpc: "AVERAGE_CPC",
  costPerConversion: "COST_PER_CONVERSION",
  conversionValuePerCost: "CONVERSION_VALUE_PER_COST",
};

/** 画面が所有し、表と保存ダイアログへ渡す分析条件。日付はAdsTopDateContextが所有する。 */
export type AdsAnalysisState = {
  filters: AdsFilterState;
  groupBy: CampaignEntityGroupBy;
  sortField: AdsMetricKey;
  sortDirection: OrderDirection;
};

/** 全体画面の初期条件。変更はReactのsetterで新しい値に置き換える。 */
export const DEFAULT_ADS_ANALYSIS_STATE: AdsAnalysisState = {
  filters: DEFAULT_ADS_FILTER,
  groupBy: "CAMPAIGN",
  sortField: "cost",
  sortDirection: "DESC",
};
// #endregion

// #region Date options — reuse UA preset names for consistency
export const AdsDateOptionSingles = [
  "latest",
  "7d",
  "14d",
  "28d",
  "3m",
  "custom",
] as const;
type AdsDateOptionSingle = (typeof AdsDateOptionSingles)[number];

const AdsDateOptionSingleLabels: Record<
  AdsDateOptionSingle,
  ReturnType<typeof msg>
> = {
  latest: msg`Latest`,
  "7d": msg`Last 7 days`,
  "14d": msg`Last 14 days`,
  "28d": msg`Last 28 days`,
  "3m": msg`Last 3 months`,
  custom: msg`Custom`,
};

export const AdsDateOptionSingleOptions = AdsDateOptionSingles.map(
  (option) => ({
    value: option,
    label: AdsDateOptionSingleLabels[option],
  }),
);
// #endregion

// #region AdsDateOptionComparison
export const AdsDateOptionComparisons = [
  "comp_7d",
  "comp_14d",
  "comp_28d",
  "comp_3m",
  "comp_this_month",
  "comp_last_month",
  "comp_custom",
] as const;
export type AdsDateOptionComparison = (typeof AdsDateOptionComparisons)[number];

export function isAdsDateOptionComparison(
  value: unknown,
): value is AdsDateOptionComparison {
  return AdsDateOptionComparisons.includes(value as AdsDateOptionComparison);
}

const AdsDateOptionComparisonLabels: Record<
  AdsDateOptionComparison,
  ReturnType<typeof msg>
> = {
  comp_7d: msg`Past 7 days vs previous 7 days`,
  comp_14d: msg`Past 14 days vs previous 14 days`,
  comp_28d: msg`Past 28 days vs previous 28 days`,
  comp_3m: msg`Past 90 days vs previous 90 days`,
  comp_this_month: msg`Current month vs same month last year`,
  comp_last_month: msg`Previous month vs same month last year`,
  comp_custom: msg`Custom Comparison`,
};

export const AdsDateOptionComparisonOptions = AdsDateOptionComparisons.map(
  (option) => ({
    value: option,
    label: AdsDateOptionComparisonLabels[option],
  }),
);
// #endregion

export type AdsDateOption = AdsDateOptionSingle | AdsDateOptionComparison;

export function isAdsDateOption(value: unknown): value is AdsDateOption {
  return (
    AdsDateOptionSingles.includes(value as AdsDateOptionSingle) ||
    isAdsDateOptionComparison(value)
  );
}
